import classNames from 'classnames';
import React, {
    ReactElement,
    useState,
    useEffect,
    ChangeEvent,
} from 'react';
import PhoneInput from 'react-phone-input-2';

import { IQATestableProps } from '../../shared/types/IQATestableProps';
import { ISetState } from '../../shared/types/ISetState';
import { IStyleableProps } from '../../shared/types/IStyleableProps';

import styles from './PhoneNumberInput.module.css';
import { rawCountries } from './vendor/rawCountries';

export enum COUNTRY {
    US = 'us',
}

export type IPhoneNumberInputProps = IQATestableProps & IStyleableProps & {
    onChange: (event: string, country: ICountryProps) => void;
    onBlur?: (event: React.ChangeEvent<HTMLInputElement>) => void;
    disabled?: boolean;
    error?: string;
    hint?: string;
    value?: string;
    placeholder?: string;
    label: string;
    type?: string;
    validate?: boolean;
    isValid?: (value: string) => IIsValidResponse;
    onError?: () => void;
    required?: boolean;
};

export type ICountryProps = {
    countryCode: string;
    format: string;
    dialCode?: string;
    name: string;
};

export type IIsValidResponse = {
    isValid: boolean;
    error: string;
};

const maskObj: { [key: string]: string } = {};

rawCountries.forEach((country: (string | number | any)[]): void => {
    const countryAbbrev: string = country[2];
    const mask: string = country[4];

    maskObj[countryAbbrev] = mask;
});

export function OzPhoneNumberInput (props: IPhoneNumberInputProps): ReactElement {
    const {
        hint,
        disabled = false,
        onChange,
        value = '',
        onBlur,
        placeholder = '',
        label = '',
        'data-testid': dataTest = '',
        className = '',
        type = 'phone',
        validate = true,
        isValid,
        onError,
        required = false,
    }: IPhoneNumberInputProps = props;

    const [isFocused, setIsFocused]: [boolean, ISetState<boolean>] = useState<boolean>(false);
    const [inputValue, setInputValue]: [string, ISetState<string>] = useState<string>(value || '');
    const [countryCode, setCountryCode]: [string, ISetState<string>] = useState<string>('1');
    const [error, setError]: [string, ISetState<string>] = useState<string>('');
    const [isTouched, setIsTouched]: [boolean, ISetState<boolean>] = useState<boolean>(false);

    useEffect((): void => {
        setInputValue(value);
    }, [value]);

    const wrapperClassName: string = classNames({
        'w-full h-8 flex flex-col justify-center items-center': true,
        'relative border border-gray-300 rounded bg-white text-black-75': true,
        '!border-red-500': (error && !disabled),
        '!border-teal-500': (isFocused && !error),
        '!bg-gray-100': disabled,
    });

    const inputClassName: string = classNames({
        '!w-full bg-transparent text-med text-gray-600 font-medium': true,
        'leading-relaxed outline-none caret-primary px-2 text-ellipsis': true,
        'text-gray-900': (isFocused || !!inputValue),
        'text-secondary-contrast caret-error': (!!error && !disabled),
    });

    const labelClassName: string = classNames({
        'w-full mb-1/2 block text-xs text-left text-gray-700': true,
        '!font-medium truncate opacity-100 leading-4': true,
        'transition-opacity ease-in-out duration-500 px-2': true,
        'hidden flex opacity-00': (!isFocused && !inputValue && !countryCode),
    });

    const errorClassName: string = classNames({
        'w-full text-base font-normal truncate leading-12 pt-1/2': true,
        'text-red-500': !!error,
    });

    const hintClassName: string = classNames({
        'w-full text-base font-normal truncate leading-12 pt-1/2': true,
        'text-gray-700': !!hint,
    });

    const handleChange: (value: string, country: ICountryProps) => void = (value: string, country: ICountryProps): void => {
        setInputValue(value);
        onChange(value, country);

        if (!isTouched) {
            setIsTouched(true);
        }

        if (value.length > 0 && country.dialCode) {
            setCountryCode(value.slice(0, country.dialCode.length));
        } else {
            setCountryCode('');
        }
    };

    const phoneNumberValid: (value: string, country: ICountryProps) => void = (value: string, country: ICountryProps): void => {
        const formatLength = (country.format.match(/\./g) || []).length;

        if (!isTouched) {
            return;
        }

        if (validate === false) {
            setError('');

            return;
        }

        if (value.length === 0 && required) {
            if (onError) {
                onError();
            }

            setError('This field is required');
        } else if (formatLength !== value.length && value.length > 0) {
            if (onError) {
                onError();
            }

            setError('Whoops, invalid number of digits!');
        } else {
            setError('');

            if (isValid) {
                const errorObj: IIsValidResponse = isValid(value);

                if (!errorObj.isValid) {
                    setError(errorObj.error);
                }
            }
        }
    };

    const handleFocus: () => void = (): void => {
        setIsFocused(true);
    };

    const handleBlur: (e: ChangeEvent<HTMLInputElement>) => void = (event: ChangeEvent<HTMLInputElement>): void => {
        setIsFocused(false);

        if (!isTouched) {
            setIsTouched(true);
        }

        if (onBlur) {
            onBlur(event);
        }
    };

    let placeholderValue: string = placeholder;

    if (isFocused) {
        placeholderValue = '';
    }

    return (
        <div className={`h-auto ${className}`} data-testid={dataTest}>
            <div className={wrapperClassName}>
                <label className={labelClassName}>{label}</label>
                <PhoneInput
                    placeholder={placeholderValue}
                    type={type}
                    country={COUNTRY.US}
                    disabled={disabled}
                    onBlur={handleBlur}
                    onFocus={handleFocus}
                    value={inputValue}
                    onChange={handleChange}
                    buttonClass={
                        `ml-1/2 hover:bg-black-10 ${styles.OzPhoneNumberInputButton}`
                    }
                    inputClass={`${inputClassName} ${styles.hiddenBorder}`}
                    isValid={phoneNumberValid}
                    specialLabel=''
                    masks={maskObj}
                />
            </div>
            {hint && !error &&
                <p className={hintClassName}>{hint}</p>}
            {error &&
                <p className={errorClassName}>{error}</p>}
        </div>
    );
}
